import React, { useEffect, useState } from "react";
import { FiArrowLeft, FiEye, FiEyeOff, FiPlus, FiUser } from "react-icons/fi";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { getAuth } from "../../services/localStorage";
import { API_ENDPOINT } from "../../services/api";
import Labels from "../../services/apiLabels";
import {
  authHeaders,
  isApiSuccess,
  listFromPayload,
  officeId,
  readJson,
} from "../Offices/officeHelpers";
import {
  EMPTY_USER_FORM,
  errorText,
  fieldErrorsFromUserApi,
  isValidEmail,
  officeLabel,
  toUserForm,
  userFormData,
  userId,
  usersFromPayload,
} from "./inspectionUserHelpers";
import "./inspectionUsers.css";

const OFFICES_PATH = "/dealers/dashbord/offices";

function InspectionUserForm() {
  const history = useHistory();
  const location = useLocation();
  const { officeId: officeIdParam, userId: userIdParam } = useParams();
  const editingId = userIdParam || null;
  const auth = getAuth();
  const listPath = `${OFFICES_PATH}/${officeIdParam}/users`;

  const [office, setOffice] = useState(location.state?.office || null);
  const [form, setForm] = useState(
    location.state?.user
      ? toUserForm(location.state.user, officeIdParam)
      : { ...EMPTY_USER_FORM, office: officeIdParam || "" }
  );
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    location.state?.user
      ? toUserForm(location.state.user, officeIdParam).image
      : ""
  );
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState({ type: "", text: "" });
  const [saving, setSaving] = useState(false);
  const [loadingUser, setLoadingUser] = useState(Boolean(editingId));
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    return () => {
      if (imagePreview && String(imagePreview).startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  useEffect(() => {
    if (!auth?.token || !officeIdParam) return undefined;
    if (
      location.state?.office &&
      String(officeId(location.state.office)) === String(officeIdParam)
    ) {
      setOffice(location.state.office);
      return undefined;
    }
    let cancelled = false;
    fetch(API_ENDPOINT(Labels.office_listing), {
      headers: authHeaders(auth.token),
    })
      .then((response) =>
        readJson(response).then((json) => ({ response, json }))
      )
      .then(({ response, json }) => {
        if (cancelled || !isApiSuccess(response, json)) return;
        const match = listFromPayload(json).find(
          (item) => String(officeId(item)) === String(officeIdParam)
        );
        if (match) setOffice(match);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [auth?.token, officeIdParam, location.state]);

  useEffect(() => {
    if (!editingId) {
      setForm((prev) => ({
        ...EMPTY_USER_FORM,
        office: officeIdParam || prev.office || "",
      }));
      setLoadingUser(false);
      return undefined;
    }
    if (
      location.state?.user &&
      String(userId(location.state.user)) === String(editingId)
    ) {
      const next = toUserForm(location.state.user, officeIdParam);
      setForm(next);
      setImagePreview(next.image);
      setLoadingUser(false);
      return undefined;
    }
    if (!auth?.token) {
      setLoadingUser(false);
      setBanner({ type: "error", text: "Please sign in again." });
      return undefined;
    }

    let cancelled = false;
    setLoadingUser(true);
    const url = `${API_ENDPOINT(
      Labels.inspection_office_users
    )}?office=${encodeURIComponent(officeIdParam)}`;
    fetch(url, { headers: authHeaders(auth.token) })
      .then((response) =>
        readJson(response).then((json) => ({ response, json }))
      )
      .then(({ response, json }) => {
        if (cancelled) return;
        if (!isApiSuccess(response, json)) throw new Error("load failed");
        const match = usersFromPayload(json).find(
          (user) => String(userId(user)) === String(editingId)
        );
        if (!match) {
          setBanner({
            type: "error",
            text: "This inspection user could not be found.",
          });
          return;
        }
        const next = toUserForm(match, officeIdParam);
        setForm(next);
        setImagePreview(next.image);
      })
      .catch(() => {
        if (!cancelled) {
          setBanner({
            type: "error",
            text: "Could not load this user. Please try again.",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingUser(false);
      });

    return () => {
      cancelled = true;
    };
  }, [auth?.token, editingId, officeIdParam, location.state]);

  const goToList = (message) => {
    history.push({
      pathname: listPath,
      state: {
        office,
        notice: message || undefined,
      },
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    if (banner.text) setBanner({ type: "", text: "" });
  };

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview((prev) => {
      if (prev && String(prev).startsWith("blob:")) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    if (errors.image) setErrors((prev) => ({ ...prev, image: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!String(form.name).trim()) next.name = "Name is required";
    if (!isValidEmail(form.email)) next.email = "Enter a valid email";
    if (!editingId && !String(form.password).trim()) {
      next.password = "Password is required";
    } else if (
      String(form.password).trim() &&
      String(form.password).length < 6
    ) {
      next.password = "Use at least 6 characters";
    }
    if (!editingId && !imageFile) next.image = "Photo is required";
    if (!String(form.office || officeIdParam).trim()) {
      next.office = "Office is required";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!auth?.token) {
      setBanner({ type: "error", text: "Please sign in again." });
      return;
    }

    setSaving(true);
    setBanner({ type: "", text: "" });
    try {
      const isEdit = editingId != null;
      const path = isEdit
        ? `${Labels.update_inspection_user}${editingId}/`
        : Labels.create_inspection_user;
      const body = userFormData(
        { ...form, office: form.office || officeIdParam },
        imageFile,
        {
          includePassword: !isEdit || Boolean(String(form.password).trim()),
        }
      );
      const response = await fetch(API_ENDPOINT(path), {
        method: isEdit ? "PATCH" : "POST",
        headers: authHeaders(auth.token),
        body,
      });
      const json = await readJson(response);
      if (!isApiSuccess(response, json)) {
        const apiFields = fieldErrorsFromUserApi(json);
        if (Object.keys(apiFields).length) {
          setErrors((prev) => ({ ...prev, ...apiFields }));
          return;
        }
        throw new Error(
          errorText(
            json,
            `Could not ${isEdit ? "update" : "create"} the inspection user.`
          )
        );
      }
      goToList(isEdit ? "Inspection user updated." : "Inspection user added.");
    } catch (err) {
      setBanner({
        type: "error",
        text: err?.message || "Could not save the user. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dd-section dd-offices dd-insp-users">
      <form
        className="dd-form dd-offices__form"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="dd-arb-intro">
          <span className="dd-arb-intro__badge" aria-hidden>
            <FiUser size={18} />
          </span>
          <div>
            <h2>{editingId ? "Edit inspection user" : "Add inspection user"}</h2>
            <p>
              {editingId
                ? `Update this inspector’s login for ${officeLabel(office)}.`
                : `Create an inspector login for ${officeLabel(office)}.`}
            </p>
          </div>
        </div>

        {banner.text ? (
          <p
            className={
              banner.type === "success"
                ? "dd-password__hint"
                : "dd-password__error"
            }
            role="status"
          >
            {banner.text}
          </p>
        ) : null}

        {loadingUser ? (
          <div className="dd-empty">
            <strong>Loading user…</strong>
          </div>
        ) : (
          <>
            <div className="dd-form-grid">
              <div className={`dd-field${errors.name ? " is-invalid" : ""}`}>
                <label htmlFor="insp-name">Name *</label>
                <input
                  id="insp-name"
                  name="name"
                  className="form-control"
                  value={form.name}
                  onChange={handleChange}
                  autoComplete="name"
                />
                {errors.name ? (
                  <p className="dd-password__error">{errors.name}</p>
                ) : null}
              </div>

              <div className={`dd-field${errors.email ? " is-invalid" : ""}`}>
                <label htmlFor="insp-email">Email *</label>
                <input
                  id="insp-email"
                  name="email"
                  type="email"
                  className="form-control"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                />
                {errors.email ? (
                  <p className="dd-password__error">{errors.email}</p>
                ) : null}
              </div>

              <div className={`dd-field${errors.password ? " is-invalid" : ""}`}>
                <label htmlFor="insp-password">
                  {editingId ? "Password" : "Password *"}
                </label>
                <div className="dd-password__input">
                  <input
                    id="insp-password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    value={form.password}
                    onChange={handleChange}
                    autoComplete="new-password"
                    placeholder={
                      editingId
                        ? "Leave blank to keep the current password"
                        : ""
                    }
                  />
                  <button
                    type="button"
                    className="dd-password__toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                </div>
                {errors.password ? (
                  <p className="dd-password__error">{errors.password}</p>
                ) : null}
              </div>

              <div
                className={`dd-field${errors.phone_number ? " is-invalid" : ""}`}
              >
                <label htmlFor="insp-phone">Phone number</label>
                <input
                  id="insp-phone"
                  name="phone_number"
                  className="form-control"
                  value={form.phone_number}
                  onChange={handleChange}
                  autoComplete="tel"
                />
                {errors.phone_number ? (
                  <p className="dd-password__error">{errors.phone_number}</p>
                ) : null}
              </div>

              <div
                className={`dd-field dd-field--full${
                  errors.address ? " is-invalid" : ""
                }`}
              >
                <label htmlFor="insp-address">Address</label>
                <input
                  id="insp-address"
                  name="address"
                  className="form-control"
                  value={form.address}
                  onChange={handleChange}
                  autoComplete="street-address"
                />
                {errors.address ? (
                  <p className="dd-password__error">{errors.address}</p>
                ) : null}
              </div>

              <div className={`dd-field${errors.image ? " is-invalid" : ""}`}>
                <label htmlFor="insp-image">
                  Photo {editingId ? "" : "*"}
                </label>
                <input
                  id="insp-image"
                  name="image"
                  type="file"
                  accept="image/*"
                  className="form-control"
                  onChange={handleImage}
                />
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt=""
                    className="dd-insp-users__preview"
                  />
                ) : null}
                {errors.image ? (
                  <p className="dd-password__error">{errors.image}</p>
                ) : null}
              </div>
            </div>

            <div className="dd-btn-row">
              <button
                type="button"
                className="dd-btn dd-btn--ghost"
                onClick={() => goToList()}
                disabled={saving}
              >
                <FiArrowLeft size={16} aria-hidden />
                Back to users
              </button>
              <button
                type="submit"
                className="dd-btn dd-btn--primary"
                disabled={saving || loadingUser}
              >
                <FiPlus size={16} aria-hidden />
                {saving
                  ? "Saving…"
                  : editingId
                    ? "Update user"
                    : "Add user"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

export default InspectionUserForm;
